import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
  noindex?: boolean;
  structuredData?: object | object[];
  speakable?: boolean;
  faqItems?: { question: string; answer: string }[];
}

const BASE_URL = (import.meta.env.VITE_SITE_URL as string | undefined)?.replace(/\/$/, '') || 'https://alliancesecuritysystems.com';

const SEO = ({
  title = 'Alliance Security Systems | Professional Security Solutions Since 1999',
  description = 'Alliance Security Systems provides professional alarm systems, HD security cameras, access control, and 24/7 live monitoring across the Greater Toronto Area. Trusted by 30,000+ businesses since 1999.',
  keywords = 'security systems, alarm systems, security cameras, access control, live monitoring, GTA security',
  canonical,
  canonicalUrl,
  ogImage = `${BASE_URL}/og-image.jpg`,
  ogType = 'website',
  noindex = false,
  structuredData,
  speakable = true,
  faqItems,
}: SEOProps) => {
  const fullTitle = title.includes('Alliance Security') ? title : `${title} | Alliance Security Systems`;

  const resolvedCanonical = canonical
    ? (canonical.startsWith('http') ? canonical : `${BASE_URL}${canonical.startsWith('/') ? '' : '/'}${canonical}`)
    : canonicalUrl
      ? (canonicalUrl.startsWith('http') ? canonicalUrl : `${BASE_URL}${canonicalUrl.startsWith('/') ? '' : '/'}${canonicalUrl}`)
      : BASE_URL;

  const SOCIAL_PROFILES = [
    'https://www.facebook.com/AllianceSecuritySystems',
    'https://www.linkedin.com/company/alliance-security-systems',
    'https://www.google.com/maps?cid=AllianceSecuritySystems',
    'https://www.youtube.com/@AllianceSecuritySystems',
    'https://twitter.com/AllianceSecurity',
    'https://www.instagram.com/alliancesecuritysystems',
  ];

  // Dedicated Organization schema for brand authority
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${BASE_URL}/#organization`,
    name: 'Alliance Security Systems',
    alternateName: 'Alliance Security',
    legalName: 'Alliance Security Systems Inc.',
    description: 'Professional security systems provider serving the Greater Toronto Area since 1999. Alarm systems, security cameras, access control, and 24/7 live monitoring.',
    url: BASE_URL,
    logo: {
      '@type': 'ImageObject',
      url: `${BASE_URL}/logos/alliance-logo.png`,
      width: 300,
      height: 80,
    },
    image: ogImage,
    telephone: '+1-888-458-9181',
    email: 'info@alliancesecuritysystems.com',
    foundingDate: '1999',
    numberOfEmployees: {
      '@type': 'QuantitativeValue',
      minValue: 25,
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: '2355 Derry Road East, Unit 1',
      addressLocality: 'Mississauga',
      addressRegion: 'ON',
      postalCode: 'L5S 1V6',
      addressCountry: 'CA',
    },
    contactPoint: [
      {
        '@type': 'ContactPoint',
        telephone: '+1-888-458-9181',
        contactType: 'customer service',
        areaServed: 'CA',
        availableLanguage: ['English', 'French'],
        hoursAvailable: {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '08:00',
          closes: '18:00',
        },
      },
      {
        '@type': 'ContactPoint',
        telephone: '+1-888-458-9181',
        contactType: 'sales',
        areaServed: 'CA',
        availableLanguage: 'English',
      },
      {
        '@type': 'ContactPoint',
        telephone: '+1-888-458-9181',
        contactType: 'technical support',
        areaServed: 'CA',
        availableLanguage: 'English',
      },
    ],
    sameAs: SOCIAL_PROFILES,
    knowsAbout: [
      'Alarm Systems',
      'Security Cameras',
      'Access Control Systems',
      'Live Video Monitoring',
      'Home Security',
      'Commercial Security',
      'CCTV Installation',
      'Smart Home Security',
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Security Services',
      itemListElement: [
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Alarm Systems', description: 'Professional alarm system installation and monitoring for homes and businesses in the GTA.' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Security Cameras', description: 'HD and 4K security camera installation with remote viewing and cloud storage.' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Access Control', description: 'Card access and keypad entry systems for commercial and industrial properties.' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Live Video Monitoring', description: '24/7 professional live video monitoring with instant alert response.' } },
      ],
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      reviewCount: '500',
      bestRating: '5',
    },
  };

  const defaultStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${BASE_URL}/#localbusiness`,
    name: 'Alliance Security Systems',
    alternateName: 'Alliance Security',
    description: 'Professional security systems provider serving the Greater Toronto Area since 1999. Alarm systems, security cameras, access control, and 24/7 live monitoring.',
    telephone: '+1-888-458-9181',
    foundingDate: '1999',
    url: BASE_URL,
    priceRange: '$$',
    image: ogImage,
    numberOfEmployees: { '@type': 'QuantitativeValue', minValue: 25 },
    parentOrganization: { '@id': `${BASE_URL}/#organization` },
    knowsAbout: [
      'Alarm Systems',
      'Security Cameras',
      'Access Control Systems',
      'Live Video Monitoring',
      'Home Security',
      'Commercial Security',
      'CCTV Installation',
      'Smart Home Security',
    ],
    areaServed: [
      { '@type': 'City', name: 'Toronto', containedInPlace: { '@type': 'AdministrativeArea', name: 'Ontario' } },
      { '@type': 'City', name: 'Mississauga' },
      { '@type': 'City', name: 'Brampton' },
      { '@type': 'City', name: 'Hamilton' },
      { '@type': 'City', name: 'Oakville' },
      { '@type': 'City', name: 'Burlington' },
      { '@type': 'City', name: 'Richmond Hill' },
      { '@type': 'City', name: 'Vaughan' },
      { '@type': 'City', name: 'Whitby' },
      { '@type': 'City', name: 'Ajax' },
      { '@type': 'City', name: 'Oshawa' },
    ],
    address: {
      '@type': 'PostalAddress',
      streetAddress: '2355 Derry Road East, Unit 1',
      addressLocality: 'Mississauga',
      addressRegion: 'ON',
      postalCode: 'L5S 1V6',
      addressCountry: 'CA',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '43.7076',
      longitude: '-79.6441',
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '08:00',
        closes: '18:00',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Saturday',
        opens: '09:00',
        closes: '17:00',
      },
    ],
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      reviewCount: '500',
      bestRating: '5',
    },
    sameAs: SOCIAL_PROFILES,
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Security Services',
      itemListElement: [
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Alarm Systems', description: 'Professional alarm system installation and monitoring for homes and businesses in the GTA.' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Security Cameras', description: 'HD and 4K security camera installation with remote viewing and cloud storage.' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Access Control', description: 'Card access and keypad entry systems for commercial and industrial properties.' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Live Video Monitoring', description: '24/7 professional live video monitoring with instant alert response.' } },
      ],
    },
  };

  // Speakable schema for voice assistants and AI
  const speakableSchema = speakable ? {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: fullTitle,
    description,
    url: resolvedCanonical,
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: ['h1', 'h2', '.speakable'],
    },
    about: {
      '@type': 'LocalBusiness',
      name: 'Alliance Security Systems',
      telephone: '+1-888-458-9181',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Mississauga',
        addressRegion: 'ON',
        addressCountry: 'CA',
      },
    },
  } : null;

  // Sitelinks Searchbox for Google
  const sitelinksSearchbox = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    url: BASE_URL,
    name: 'Alliance Security Systems',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${BASE_URL}/faq?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  // FAQ schema
  const faqSchema = faqItems && faqItems.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  } : null;

  useEffect(() => {
    document.title = fullTitle;

    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    const updateLinkTag = (rel: string, href: string) => {
      let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }
      element.href = href;
    };

    // Basic meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    updateMetaTag('last-modified', new Date().toISOString().split('T')[0]);

    // AI search optimization meta tags
    updateMetaTag('robots', noindex ? 'noindex, nofollow' : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1');

    // Perplexity / AI crawler hints
    updateMetaTag('ai-content-declaration', 'human-authored');

    // Geo meta tags
    updateMetaTag('geo.region', 'CA-ON');
    updateMetaTag('geo.placename', 'Mississauga');
    updateMetaTag('geo.position', '43.7076;-79.6441');
    updateMetaTag('ICBM', '43.7076, -79.6441');

    // Canonical link
    updateLinkTag('canonical', resolvedCanonical);

    // Open Graph tags
    updateMetaTag('og:title', fullTitle, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:url', resolvedCanonical, true);
    updateMetaTag('og:type', ogType, true);
    updateMetaTag('og:image', ogImage, true);
    updateMetaTag('og:site_name', 'Alliance Security Systems', true);
    updateMetaTag('og:locale', 'en_CA', true);

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', fullTitle);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', ogImage);

    // Remove all previously injected structured data scripts
    const existingScripts = document.querySelectorAll('script[data-seo-ld]');
    existingScripts.forEach((s) => s.remove());

    // Build the list of structured data items to inject
    const customItems = structuredData
      ? (Array.isArray(structuredData) ? structuredData : [structuredData])
      : null;

    const dataItems: object[] = customItems
      ? [...customItems, defaultStructuredData, organizationSchema]
      : [defaultStructuredData, organizationSchema];

    dataItems.forEach((data, idx) => {
      const script = document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.setAttribute('data-seo-ld', `${idx}`);
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
    });

    // Speakable schema
    if (speakableSchema) {
      const speakScript = document.createElement('script');
      speakScript.setAttribute('type', 'application/ld+json');
      speakScript.setAttribute('data-seo-ld', 'speakable');
      speakScript.textContent = JSON.stringify(speakableSchema);
      document.head.appendChild(speakScript);
    }

    // Sitelinks Searchbox
    const searchScript = document.createElement('script');
    searchScript.setAttribute('type', 'application/ld+json');
    searchScript.setAttribute('data-seo-ld', 'searchbox');
    searchScript.textContent = JSON.stringify(sitelinksSearchbox);
    document.head.appendChild(searchScript);

    // FAQ schema
    if (faqSchema) {
      const faqScript = document.createElement('script');
      faqScript.setAttribute('type', 'application/ld+json');
      faqScript.setAttribute('data-seo-ld', 'faq');
      faqScript.textContent = JSON.stringify(faqSchema);
      document.head.appendChild(faqScript);
    }

    return () => {
      const scripts = document.querySelectorAll('script[data-seo-ld]');
      scripts.forEach((s) => s.remove());
    };
  }, [fullTitle, description, keywords, resolvedCanonical, ogImage, ogType, noindex, structuredData, speakable, faqItems]);

  return null;
};

export default SEO;
